import { useEffect, useMemo, useState } from "react";
import { Box, Card, CardContent, Grid, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { Receipt, Savings } from "@mui/icons-material";
import { GridColDef } from "@mui/x-data-grid";
import { postData } from "../../services/base.service";
import { useNotification } from "../Shared/NotificationProvider";
import EditableDataGrid from "../Shared/EditableDataGrid";
import { ProjectDetail, ProjectInvoiceItem } from "../../types/projectTypes";
import { formatMoney, formatMoneyExact, formatPercent, numberOrNull, toDateInput } from "./projectUtils";
import ProjectProgress from "./ProjectProgress";

type ProjectFinanceTabProps = {
    detail: ProjectDetail;
    isEditMode: boolean;
    setLoading: Function;
    refresh: () => Promise<void>;
};

type InvoiceRow = {
    id: number;
    saved: boolean;
    invoice_number: string;
    invoice_date: string;
    amount: number | string;
    currency: string;
    recipient: string;
    subject: string;
    paid_date: string;
    paid_amount: number | string | null;
    comment: string;
};

const columns: GridColDef[] = [
    { field: 'invoice_number', headerName: 'Invoice no. *', flex: 0.9, editable: true },
    { field: 'invoice_date', headerName: 'Date *', flex: 0.8, editable: true, type: 'date',
        valueGetter: (params) => (params.value ? new Date(params.value) : null) },
    { field: 'amount', headerName: 'Amount *', flex: 0.8, editable: true, type: 'number' },
    { field: 'currency', headerName: 'Cur. *', flex: 0.5, editable: true, type: 'singleSelect', valueOptions: ['EUR', 'USD'] },
    { field: 'recipient', headerName: 'Recipient', flex: 0.8, editable: true },
    { field: 'subject', headerName: 'Subject', flex: 1, editable: true },
    { field: 'paid_date', headerName: 'Paid on', flex: 0.8, editable: true, type: 'date',
        valueGetter: (params) => (params.value ? new Date(params.value) : null) },
    { field: 'paid_amount', headerName: 'Paid', flex: 0.8, editable: true, type: 'number' },
    { field: 'comment', headerName: 'Comment', flex: 1, editable: true },
];

const toRow = (invoice: ProjectInvoiceItem): InvoiceRow => ({
    id: invoice.ID,
    saved: true,
    invoice_number: invoice.invoice_number,
    invoice_date: toDateInput(invoice.invoice_date),
    amount: invoice.amount,
    currency: invoice.currency,
    recipient: invoice.recipient || '',
    subject: invoice.subject || '',
    paid_date: toDateInput(invoice.paid_date),
    paid_amount: invoice.paid_amount,
    comment: invoice.comment || '',
});

// EditableDataGrid's add/edit dialog hands back Date objects or strings for date columns.
const dateValue = (value: unknown): string | null => {
    if (!value) return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value.toISOString().slice(0, 10);
    return String(value).slice(0, 10);
};

const payload = (row: InvoiceRow) => ({
    invoice_number: String(row.invoice_number || '').trim(),
    invoice_date: dateValue(row.invoice_date),
    amount: numberOrNull(row.amount),
    currency: row.currency || 'EUR',
    recipient: row.recipient || null,
    subject: row.subject || null,
    paid_date: dateValue(row.paid_date),
    paid_amount: numberOrNull(row.paid_amount),
    comment: row.comment || null,
});

function Figure({ label, value, sub }: { label: string; value: string; sub?: string }) {
    return (
        <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent sx={{ paddingY: 1.25, '&:last-child': { paddingBottom: 1.25 } }}>
                <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.2 }}>{label}</Typography>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>{value}</Typography>
                {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
            </CardContent>
        </Card>
    );
}

function ProjectFinanceTab({ detail, isEditMode, setLoading, refresh }: ProjectFinanceTabProps) {
    const notify = useNotification();
    const projectId = detail.project.ID;
    const figures = detail.figures;
    const currency = detail.project.currency;
    const [rows, setRows] = useState<InvoiceRow[]>(detail.invoices.map(toRow));

    useEffect(() => { setRows(detail.invoices.map(toRow)); }, [detail.invoices]);

    // Invoiced amount per year and quarter (like the Excel "Invoiced Qx" columns).
    const byQuarter = useMemo(() => {
        const map = new Map<string, number[]>();
        detail.invoices.forEach((invoice) => {
            const date = new Date(invoice.invoice_date);
            if (Number.isNaN(date.getTime())) return;
            const year = String(date.getFullYear());
            const quarters = map.get(year) || [0, 0, 0, 0];
            quarters[Math.floor(date.getMonth() / 3)] += Number(invoice.amount);
            map.set(year, quarters);
        });
        return Array.from(map.entries()).sort((a, b) => b[0].localeCompare(a[0]));
    }, [detail.invoices]);

    const handleChange = async (wrapper: { rows: InvoiceRow[] }) => {
        const next = wrapper.rows || [];
        const additions = next.filter((row) => !row.saved);
        const removals = rows.filter((p) => !next.some((row) => row.id === p.id));
        const updates = next.filter((row) => {
            const before = rows.find((p) => p.id === row.id);
            return row.saved && before && JSON.stringify(payload(before)) !== JSON.stringify(payload(row));
        });
        for (const row of [...additions, ...updates]) {
            const p = payload(row);
            if (!p.invoice_number || !p.invoice_date || p.amount === null) {
                notify('Invoice number, date and amount are required.', 'warning');
                return;
            }
        }

        setLoading(true);
        try {
            for (const row of removals) await postData('projects/invoices/remove', { id: row.id });
            for (const row of additions) await postData('projects/invoices/add', { projectId, ...payload(row) });
            for (const row of updates) await postData('projects/invoices/update', { id: row.id, ...payload(row) });
            await refresh();
        } catch (error) {
            notify((error as Error).message, 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box paddingTop={2}>
            <Stack direction="row" alignItems="center" gap={1} sx={{ marginBottom: 1.5 }}>
                <Savings /><Typography variant="h6">Figures</Typography>
            </Stack>
            <Grid container spacing={1.5}>
                <Grid item xs={6} md={4}>
                    <Figure label="Contract volume" value={formatMoney(figures?.contract_volume, currency)}
                        sub={figures?.intee_share !== null && figures?.intee_share !== undefined ? `INT share ${formatPercent(figures.intee_share)} = ${formatMoney(figures.int_volume, currency)}` : undefined} />
                </Grid>
                <Grid item xs={6} md={4}>
                    <Figure label="Invoiced" value={formatMoney(figures?.invoiced_total, currency)} sub={`Paid ${formatMoney(figures?.paid_total, currency)}`} />
                </Grid>
                <Grid item xs={6} md={4}>
                    <Figure label="Open INT volume" value={formatMoney(figures?.int_open_volume, currency)}
                        sub={figures?.months_remaining !== null && figures?.months_remaining !== undefined ? `${figures.months_remaining} months remaining` : undefined} />
                </Grid>
            </Grid>
            {figures && (
                <Box sx={{ marginTop: 2.5 }}>
                    <ProjectProgress item={figures} variant="full" />
                </Box>
            )}

            {byQuarter.length > 0 && (
                <Table size="small" sx={{ marginTop: 3 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Year</TableCell>
                            {['Q1', 'Q2', 'Q3', 'Q4'].map((q) => <TableCell key={q} align="right">{q}</TableCell>)}
                            <TableCell align="right">Total</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {byQuarter.map(([year, quarters]) => (
                            <TableRow key={year}>
                                <TableCell>{year}</TableCell>
                                {quarters.map((value, i) => <TableCell key={i} align="right">{value ? formatMoneyExact(value) : '-'}</TableCell>)}
                                <TableCell align="right" sx={{ fontWeight: 600 }}>{formatMoneyExact(quarters.reduce((a, b) => a + b, 0))}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}

            <Stack direction="row" alignItems="center" gap={1} sx={{ marginTop: 4, marginBottom: 1 }}>
                <Receipt /><Typography variant="h6">Client invoices</Typography>
            </Stack>
            <EditableDataGrid
                col={columns}
                rows={rows}
                editIsOn={isEditMode}
                formData={{ rows }}
                setFormData={handleChange}
                dataPath="rows"
            />
        </Box>
    );
}

export default ProjectFinanceTab;
